// Client-side helper for capturing/reading the ?ref=SLUG affiliate attribution.
// Stored in localStorage with a 30-day TTL.
import { trackAffiliateClick } from "./affiliates.functions";

const KEY = "routify_ref";
const TTL_MS = 30 * 24 * 60 * 60 * 1000;

type Stored = { slug: string; code?: string; discount_percent?: number; ts: number };

function read(): Stored | null {
  try {
    if (typeof window === "undefined") return null;
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Stored;
    if (!parsed?.slug || !parsed.ts) return null;
    if (Date.now() - parsed.ts > TTL_MS) {
      window.localStorage.removeItem(KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function write(v: Stored) {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(v));
  } catch {
    // ignore quota / storage-disabled
  }
}

export function getStoredAffiliateSlug(): string | null {
  return read()?.slug ?? null;
}

export function getStoredAffiliateCode(): string | null {
  return read()?.code ?? null;
}

export function getStoredAffiliateRef(): Stored | null {
  return read();
}

export const AFFILIATE_REF_TTL_MS = TTL_MS;

/**
 * Reads `?ref=SLUG` from the current URL and, if present, stores it (30d TTL)
 * and pings the server to log a click + resolve the promo code.
 */
export async function captureAffiliateRefFromUrl(): Promise<void> {
  if (typeof window === "undefined") return;
  const params = new URLSearchParams(window.location.search);
  const raw = params.get("ref");
  if (!raw) return;
  const slug = raw.trim().toUpperCase();
  if (!/^[A-Z0-9_-]{3,48}$/.test(slug)) return;

  const existing = read();
  if (existing && existing.slug === slug) {
    // Rolling renewal: a repeat click on the same ref link resets the
    // 30-day attribution window instead of expiring on the original date.
    write({ ...existing, ts: Date.now() });
    return;
  }

  write({ slug, ts: Date.now() });

  try {
    const res = await trackAffiliateClick({ data: { slug } });
    if (res.ok) {
      write({ slug, code: res.code, discount_percent: res.discount_percent, ts: Date.now() });
    } else {
      // Invalid/expired ref → drop it so it doesn't dangle in storage.
      try { window.localStorage.removeItem(KEY); } catch { /* noop */ }
    }
  } catch {
    // Network fail — keep the slug; server will still recognize it later.
  }
}
